import type { Dispatch, SetStateAction } from "react";
import { TaskType } from "@common/types/task";
import type { CloseTemplateBasis } from "@common/types/closeTemplateBasis";
import { Picker } from "@design-system/Picker/Picker.lib";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import { LockedTaskTypePicker } from "./LockedTaskTypePicker";
import { DocumentRetrievalFields } from "../DocumentRetrievalFields";
import { PeriodDateFields } from "../PeriodDateFields";
import { ReconciliationFields } from "../ReconciliationFields";
import { ReviewFields } from "../ReviewFields";
import { TRANSACTIONS_VIEW_TYPE_LABEL } from "../closeTemplateTasksDrawer.constants";
import type { TransactionsViewType } from "../closeTemplateTaskViewConfig";

interface EditTaskWorkflowFieldsProps {
    taskType: TaskType;
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: Dispatch<
        SetStateAction<Record<string, unknown> | null>
    >;
    transactionsViewType: TransactionsViewType;
    basis?: CloseTemplateBasis | null;
}

export const EditTaskWorkflowFields = ({
    taskType,
    viewConfig,
    onViewConfigChange,
    transactionsViewType,
    basis,
}: EditTaskWorkflowFieldsProps) => (
    <>
        <DescriptionListItem label="Action">
            <div className={taskStyles.detailSelectWrapper}>
                <LockedTaskTypePicker
                    taskType={taskType}
                    triggerClassName={taskStyles.detailSelectTrigger}
                />
            </div>
        </DescriptionListItem>

        {taskType === TaskType.TRANSACTIONS && (
            <DescriptionListItem label="View type">
                <div className={taskStyles.detailSelectWrapper}>
                    <Picker.Select
                        items={[
                            {
                                value: transactionsViewType,
                                label: TRANSACTIONS_VIEW_TYPE_LABEL[
                                    transactionsViewType
                                ],
                            },
                        ]}
                        value={transactionsViewType}
                        disabled
                        trigger={
                            <Picker.Button
                                variant="primary"
                                className={taskStyles.detailSelectTrigger}
                            />
                        }
                    />
                </div>
            </DescriptionListItem>
        )}

        {taskType === TaskType.TRANSACTION_REVIEW && (
            <ReviewFields
                viewConfig={viewConfig}
                onViewConfigChange={onViewConfigChange}
                basis={basis}
            />
        )}

        {(taskType === TaskType.PROFIT_LOSS ||
            taskType === TaskType.BALANCE_SHEET) && (
            <PeriodDateFields
                htmlIdPrefix="editTask"
                taskType={
                    taskType === TaskType.BALANCE_SHEET
                        ? TaskType.BALANCE_SHEET
                        : TaskType.PROFIT_LOSS
                }
                viewConfig={viewConfig}
                onViewConfigChange={onViewConfigChange}
            />
        )}

        {taskType === TaskType.DOCUMENT_RETRIEVAL && (
            <DocumentRetrievalFields
                htmlIdPrefix="editTask"
                viewConfig={viewConfig}
                onViewConfigChange={onViewConfigChange}
            />
        )}

        {taskType === TaskType.RECONCILIATION && (
            <ReconciliationFields
                viewConfig={viewConfig}
                onViewConfigChange={onViewConfigChange}
            />
        )}
    </>
);
